import { useId, useState } from 'react';
import { useMutation, useQueries } from '@tanstack/react-query';
import type { PortfolioCompanyDetail } from '@nemo/api';
import { STAGES, STAGE_LABELS } from '@nemo/core/model';
import { useFundNameSearch } from '../grid/useFundNameSearch.js';
import { useTRPC } from '../trpc.js';
import { Select } from '../ui/Select.js';
import { useToast } from '../ui/Toast.js';

/** "Other…" in the label select: the round was called something else. */
const OTHER_LABEL = '__other';

/** The round labels the app already reads rounds by, as a person says them. */
const LABEL_OPTIONS = [
  ...STAGES.map((stage) => ({ value: STAGE_LABELS[stage], label: STAGE_LABELS[stage] })),
  { value: OTHER_LABEL, label: 'Other…' },
];

const CURRENCIES = ['EUR', 'USD', 'GBP', 'CHF'].map((code) => ({ value: code, label: code }));

const NO_LEAD = '';

function thisMonth(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

/**
 * One round, said the way people say one: what it was called, when, how much,
 * who was in it and who led. Investors are picked from the fund list or typed;
 * a typed name is matched the way the Quantium import matches and added as a
 * fund when nothing answers, so the round never waits on the list.
 */
export function AddRound(props: {
  company: PortfolioCompanyDetail;
  onDone: () => void;
  onClose: () => void;
}): React.JSX.Element {
  const trpc = useTRPC();
  const toast = useToast();
  const listId = useId();
  const [label, setLabel] = useState('Series A');
  const [otherLabel, setOtherLabel] = useState('');
  const [month, setMonth] = useState(thisMonth());
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('EUR');
  const [participants, setParticipants] = useState<string[]>([]);
  const [typing, setTyping] = useState('');
  const [lead, setLead] = useState(NO_LEAD);
  const search = useFundNameSearch(typing);
  // A fund of exactly the name comes first, so one answer each says whether it is new.
  const participantFunds = useQueries({
    queries: participants.map((name) => trpc.funds.search.queryOptions({ text: name, limit: 1 })),
  });

  const add = useMutation(
    trpc.portfolio.addRound.mutationOptions({
      onSuccess: ({ added }) => {
        const created =
          added.fundsCreated.length === 0
            ? ''
            : ` Added as funds: ${added.fundsCreated.join(', ')}.`;
        toast(`${roundLabel()} recorded in ${props.company.name}.${created}`, { tone: 'info' });
        props.onDone();
      },
      onError: (error: { message: string }) => toast(error.message),
    }),
  );

  function roundLabel(): string {
    return label === OTHER_LABEL ? otherLabel.trim() : label;
  }

  function addTyped(): void {
    const names = typing
      .split(',')
      .map((name) => name.trim())
      .filter((name) => name !== '');
    setTyping('');
    if (names.length === 0) return;
    setParticipants((current) => [
      ...current,
      ...names.filter(
        (name) => !current.some((existing) => existing.toLowerCase() === name.toLowerCase()),
      ),
    ]);
  }

  function removeParticipant(name: string): void {
    setParticipants((current) => current.filter((existing) => existing !== name));
    if (lead === name) setLead(NO_LEAD);
  }

  const amountValue = amount.trim() === '' ? null : Number(amount) * 1_000_000;
  const ready =
    roundLabel() !== '' &&
    /^\d{4}-\d{2}$/.test(month) &&
    participants.length > 0 &&
    (amountValue === null || (Number.isFinite(amountValue) && amountValue > 0));

  return (
    <form
      className="log-contact add-round"
      onSubmit={(event) => {
        event.preventDefault();
        if (!ready) return;
        add.mutate({
          portfolioCompanyId: props.company.id,
          roundLabel: roundLabel(),
          month,
          amount: amountValue,
          currency: amountValue === null ? null : currency,
          lead: lead === NO_LEAD ? null : lead,
          participants,
        });
      }}
      onKeyDown={(event) => {
        if (event.key !== 'Escape') return;
        event.stopPropagation();
        props.onClose();
      }}
    >
      <div className="log-contact-row">
        <Select
          label="Round"
          className="log-contact-channel"
          value={label}
          onChange={setLabel}
          options={LABEL_OPTIONS}
        />
        {label === OTHER_LABEL && (
          <input
            autoFocus
            className="log-contact-person"
            aria-label="What the round was called"
            placeholder="Bridge, Series D, …"
            value={otherLabel}
            onChange={(event) => setOtherLabel(event.target.value)}
          />
        )}
        <input
          type="month"
          aria-label="Month"
          className="add-round-month"
          value={month}
          onChange={(event) => setMonth(event.target.value)}
        />
      </div>

      <div className="log-contact-row">
        <input
          className="add-round-amount"
          inputMode="decimal"
          aria-label="Amount, in millions"
          placeholder="Amount, millions"
          value={amount}
          onChange={(event) => setAmount(event.target.value)}
        />
        <Select
          label="Currency"
          className="add-round-currency"
          value={currency}
          onChange={setCurrency}
          options={CURRENCIES}
        />
      </div>

      <div className="add-round-participants">
        {participants.map((name, index) => {
          const found = participantFunds[index];
          // Not called new until the answer says so.
          const known =
            found === undefined ||
            found.isPending ||
            found.data?.[0]?.name.toLowerCase() === name.toLowerCase();
          return (
            <span
              className={known ? 'owner-chip participant-chip' : 'chip-hollow participant-chip'}
              key={name}
              title={known ? undefined : 'No fund by this name: matched or added as a fund'}
            >
              {name}
              {!known && ' · new'}
              <button
                type="button"
                className="participant-remove"
                aria-label={`Take ${name} off the round`}
                onClick={() => removeParticipant(name)}
              >
                ✕
              </button>
            </span>
          );
        })}
        <input
          list={listId}
          aria-label="Add an investor"
          placeholder={participants.length === 0 ? 'Investors in the round' : 'Add another'}
          value={typing}
          onChange={(event) => {
            // Picking from the list fills the whole name in at once.
            const value = event.target.value;
            if (search.matches.some((fund) => fund.name.toLowerCase() === value.toLowerCase())) {
              setTyping('');
              setParticipants((current) =>
                current.some((existing) => existing.toLowerCase() === value.toLowerCase())
                  ? current
                  : [...current, value],
              );
            } else {
              setTyping(value);
            }
          }}
          onKeyDown={(event) => {
            if (event.key !== 'Enter' && event.key !== ',') return;
            event.preventDefault();
            addTyped();
          }}
          onBlur={addTyped}
        />
        <datalist id={listId}>
          {search.matches.map((fund) => (
            <option key={fund.id} value={fund.name} />
          ))}
        </datalist>
      </div>

      <div className="log-contact-row">
        <Select
          label="Lead"
          className="log-contact-person"
          value={lead}
          onChange={setLead}
          options={[
            { value: NO_LEAD, label: 'No lead named' },
            ...participants.map((name) => ({ value: name, label: `Led by ${name}` })),
          ]}
        />
      </div>

      <div className="log-contact-row">
        <button className="button small primary" type="submit" disabled={add.isPending || !ready}>
          Add round
        </button>
        <button className="button small" type="button" onClick={props.onClose}>
          Cancel
        </button>
      </div>
    </form>
  );
}
